import * as React from "react";

import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import InlineAlert from "@/components/providers/inline-alert-msg";
import { getErrorMessage } from "@/hooks/redux-hooks";
import { useNavigate } from "react-router";
import { ssoApi } from "@/redux/sso/sso-api";
import { AnimatePresence, motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface VerifyAccountFormProps extends React.HTMLAttributes<HTMLDivElement> {
  ctaText: string;
  username: string;
  nextPage: string;
  password?: string;
  toggleVisiblity: () => void;
  verificationType: SSOVerificationScope;
}

const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0 },
};

const createFormSchema = (verificationType: SSOVerificationScope) => {
  const baseSchema = {
    otp: z.string().min(4).max(6),
    password: z.string().optional(),
  };

  if (verificationType === "reset_password") {
    return z.object({
      ...baseSchema,
      password: z.string().min(6, {
        message: "Your password must be at least 6 characters",
      }),
    });
  }

  return z.object(baseSchema);
};

export default function VerifyAccountForm({
  ctaText,
  username,
  password,
  nextPage,
  className,
  toggleVisiblity,
  verificationType,
}: VerifyAccountFormProps) {
  const { toast } = useToast();

  const isResetPassword = verificationType === "reset_password";
  const isLogin = verificationType === "login";

  const FormSchema = createFormSchema(verificationType);

  const [showPassword, setShowPassword] = React.useState(false);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  const [
    resendVerification,
    {
      isLoading: resendLoading,
      isError: verificationSentFailed,
      error: verificationSentError,
    },
  ] = ssoApi.useResendVerificationMutation();

  const [
    verify,
    {
      isLoading: verificationLoading,
      isError: verificationFailed,
      error: verificationError,
      isSuccess: verificationSuccessful,
    },
  ] = ssoApi.useCompleteVerificationMutation();

  const [
    completeMfa,
    { isLoading: mfaLoading, isError: mfaFailed, error: mfaError },
  ] = ssoApi.useCompleteVerificationMutation();

  const [resendCounter, setResendCounter] = React.useState<number>(60);
  React.useEffect(() => {
    if (resendCounter > 0) {
      setTimeout(() => setResendCounter(resendCounter - 1), 1000);
    }
  }, [resendCounter]);

  const handleResendVerification = async () => {
    const res: SSOResendVerificationPayload = {
      username: username,
      password: password,
      verification_type: verificationType,
    };
    await resendVerification(res).unwrap();
    setResendCounter(60);
  };

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    const res: SSOCompleteVerificationPayload = {
      otp: data.otp,
      username: username,
      password: data.password,
    };

    if (isLogin) await completeMfa(res).unwrap();
    else await verify(res).unwrap();
  };

  let navigate = useNavigate();

  React.useEffect(() => {
    if (verificationSuccessful) {
      toast({
        title: isResetPassword
          ? "Reset password Successful"
          : "Verification Successful",
        description: isResetPassword
          ? "You have successful reset your password. Kindly proceed to login"
          : "Account verification is successful",
      });
      navigate(nextPage);
    }
  }, [verificationSuccessful]);

  const isLoading = resendLoading || verificationLoading || mfaLoading;
  const isError = verificationSentFailed || verificationFailed || mfaFailed;
  const error = verificationSentFailed
    ? verificationSentError
    : verificationFailed
    ? verificationError
    : mfaError;

  return (
    <motion.div
      className={cn("grid gap-6", className)}
      initial="hidden"
      animate="visible"
      variants={formVariants}
    >
      {isError && error && (
        <InlineAlert
          title={
            verificationSentError
              ? "Unable to send verification code"
              : "Verification Failed"
          }
          message={getErrorMessage(error)}
          mtype="error"
        />
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <motion.div className="grid gap-2" variants={itemVariants}>
            <motion.div className="grid gap-1" variants={itemVariants}>
              <FormField
                control={form.control}
                name="otp"
                render={({ field }) => (
                  <FormItem>
                    <motion.div variants={itemVariants}>
                      <FormLabel>One-Time Password</FormLabel>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                      <FormControl>
                        <InputOTP maxLength={6} {...field}>
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSeparator />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </FormControl>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                      <FormDescription>
                        Please enter the one-time password sent to you.{" "}
                        <AnimatePresence mode="wait">
                          {resendCounter > 0 ? (
                            <motion.span
                              key="counter"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              Resend OTP in {resendCounter}
                            </motion.span>
                          ) : (
                            <motion.span
                              key="button"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              <Button
                                variant="link"
                                disabled={isLoading}
                                onClick={handleResendVerification}
                              >
                                Resend OTP
                              </Button>
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </FormDescription>
                      <FormMessage />
                    </motion.div>
                  </FormItem>
                )}
              />
            </motion.div>

            {isResetPassword && (
              <motion.div className="grid gap-1" variants={itemVariants}>
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            disabled={isLoading}
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </motion.div>
            )}

            <motion.div
              className="flex items-center justify-between gap-4 mt-2"
              variants={itemVariants}
            >
              <span className="text-muted-foreground text-sm">
                <Button variant="link" onClick={() => toggleVisiblity()}>
                  Go Back
                </Button>
              </span>
              <Button disabled={isLoading} className="flex-shrink-0">
                {isLoading && (
                  <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                )}
                {ctaText}
              </Button>
            </motion.div>
          </motion.div>
        </form>
      </Form>
    </motion.div>
  );
}
